import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { invoiceService } from '@/services/firebase';
import { Invoice } from '@/types';
import LoadingSpinner from '@/components/LoadingSpinner';
import PaymentModal from '@/components/PaymentModal';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { downloadInvoicePdf } from '@/utils/invoicePdf';

export default function CustomerInvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const handleDownloadPdf = async () => {
    if (!invoice) return;
    setPdfBusy(true);
    try {
      await downloadInvoicePdf(invoice);
    } catch (err) {
      console.error('Error generating invoice PDF:', err);
      toast.error('Could not generate the PDF. Please try again.');
    } finally {
      setPdfBusy(false);
    }
  };

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const invoiceData = await invoiceService.getInvoice(id);
        if (invoiceData) {
          setInvoice(invoiceData);
        } else {
          setError('Invoice not found');
        }
      } catch (err) {
        console.error('Error fetching invoice:', err);
        setError('Failed to load invoice details');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [id]);

  const handlePaymentSuccess = () => {
    // Refresh the invoice data
    if (id) {
      console.log('Refreshing invoice data after payment...');
      invoiceService.getInvoice(id).then((updatedInvoice) => {
        console.log('Updated invoice data:', updatedInvoice);
        console.log('Payment data:', updatedInvoice?.paymentData);
        setInvoice(updatedInvoice);
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: invoice?.currency || 'USD'
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'sent':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !invoice) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Invoice Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/portal/invoices" className="text-blue-600 hover:text-blue-800">
            ← Back to Invoices
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Invoice #{invoice.invoiceNumber}</h1>
          <p className="text-gray-600 mt-1">Created on {new Date(invoice.createdAt).toLocaleDateString()}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-0">
          <button
            onClick={handleDownloadPdf}
            disabled={pdfBusy}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-6 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            {pdfBusy ? 'Preparing…' : 'Download PDF'}
          </button>
          {invoice.status === 'pending' ? (
            <div className="flex gap-2">
              <span className="px-6 py-2 bg-orange-100 text-orange-800 rounded-lg font-medium">
                Payment Pending
              </span>
              <button
                onClick={() => setIsPaymentModalOpen(true)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Edit Payment
              </button>
            </div>
          ) : invoice.status !== 'paid' ? (
            <button
              onClick={() => setIsPaymentModalOpen(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Pay Now
            </button>
          ) : null}
          <Link
            to="/portal/invoices"
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-center"
          >
            Back to Invoices
          </Link>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Invoice Content */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Invoice Details</h2>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(invoice.status)}`}>
                  {invoice.status.toUpperCase()}
                </span>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(invoice.total)}</p>
                <p className="text-sm text-gray-500">Total Amount</p>
              </div>
            </div>

            {/* Invoice Items */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Items</h3>
              <div className="space-y-3">
                {invoice.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{item.description}</p>
                      <p className="text-sm text-gray-500">
                        Qty: {item.quantity} × {formatCurrency(item.unitPrice)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-800">{formatCurrency(item.total)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="border-t pt-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span>{formatCurrency(invoice.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax</span>
                  <span>{formatCurrency(invoice.tax)}</span>
                </div>
                {invoice.freight && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Freight</span>
                    <span>{formatCurrency(invoice.freight)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-semibold border-t pt-2">
                  <span>Total</span>
                  <span className="text-blue-600">{formatCurrency(invoice.total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Shipment Information */}
          {invoice.shipment && (
            <div className="bg-white shadow-md rounded-lg p-6 mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Related Shipment</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Tracking Number</p>
                  <p className="font-medium">{invoice.shipment.trackingNumber || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <p className="font-medium capitalize">{invoice.shipment.status.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Origin</p>
                  <p className="font-medium">{invoice.shipment.origin?.city || 'N/A'}, {invoice.shipment.origin?.country || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Destination</p>
                  <p className="font-medium">{invoice.shipment.destination?.city || 'N/A'}, {invoice.shipment.destination?.country || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Information */}
          {invoice.paymentData && (
            <div className="bg-white shadow-md rounded-lg p-6 mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Payment Information</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Payment Method:</span>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    invoice.paymentData.paymentMethod === 'cash' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {invoice.paymentData.paymentMethod === 'cash' ? 'Cash Payment' : 'Bank Transfer'}
                  </span>
                </div>
                
                {invoice.paymentData.uploadedAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Payment Date:</span>
                    <span className="text-sm text-gray-600">
                      {new Date(invoice.paymentData.uploadedAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
                
                {invoice.paymentData.receiptImageUrl && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Payment Receipt:</p>
                    <a 
                      href={invoice.paymentData.receiptImageUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-block"
                    >
                      <img 
                        src={invoice.paymentData.receiptImageUrl} 
                        alt="Payment Receipt" 
                        className="w-40 h-40 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity"
                      />
                    </a>
                    <p className="text-xs text-gray-500 mt-1">Click to view full size</p>
                  </div>
                )}
                
                {invoice.status === 'pending' && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      ⏳ Your payment is pending confirmation. We'll update the status once verified.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {invoice.notes && (
            <div className="bg-white shadow-md rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Notes</h3>
              <p className="text-gray-700">{invoice.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Invoice Information</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Invoice Number</p>
                <p className="font-medium">{invoice.invoiceNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Due Date</p>
                <p className="font-medium">{new Date(invoice.dueDate).toLocaleDateString()}</p>
              </div>
              {invoice.paidDate && (
                <div>
                  <p className="text-sm text-gray-600">Paid Date</p>
                  <p className="font-medium">{new Date(invoice.paidDate).toLocaleDateString()}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600">Currency</p>
                <p className="font-medium">{invoice.currency}</p>
              </div>
            </div>
          </div>

          <div className="bg-white shadow-md rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Customer Information</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">{invoice.customer.name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{invoice.customer.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-medium">{invoice.customer.phone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-medium">
                  {invoice.customer.address?.street || 'N/A'}<br />
                  {invoice.customer.address?.city || 'N/A'}, {invoice.customer.address?.state || 'N/A'} {invoice.customer.address?.zipCode || 'N/A'}<br />
                  {invoice.customer.address?.country || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        invoice={invoice}
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
} 